import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useCallback } from 'react';

import { type EPEventPayload, type POEventPayload } from './types';

export type PostOfficeAnalyticsHookScope = {
	fireAnalyticsEvent: (event: POEventPayload, attributes?: Record<string, unknown>) => void;
};

export type EngagementPlatformAnalyticsHookScope = {
	fireAnalyticsEvent: (event: EPEventPayload, attributes?: Record<string, unknown>) => void;
};

export const ANALYTICS_CHANNEL = 'postOffice';
export const EP_ANALYTICS_CHANNEL = 'engagementPlatform';
/**
 * Hook to fire track and ui analytics events for Post Office. All events will be fired with the channel `postOffice`. PlacementId will be added to attributes via PostOfficeAnalyticsContext.
 * @returns {Object} - Object containing `fireAnalyticsEvent` function.
 */
export const useFirePostOfficeAnalyticsEvents = (): PostOfficeAnalyticsHookScope => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent = useCallback(
		<Attributes extends Record<string, unknown>>(
			eventPayload: POEventPayload,
			attributes?: Attributes,
		) => {
			createAnalyticsEvent({
				...eventPayload,
				side: 'client',
				attributes: {
					...eventPayload.attributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	return {
		fireAnalyticsEvent,
	};
};

/**
 *
 * @returns Hook to fire track and ui analytics events for EP migrated messages.
 * All events will be fired with the channel `engagementPlatform`.
 * PlacementId will be added to attributes via PostOfficeAnalyticsContext.
 *  @returns {Object} - Object containing `fireAnalyticsEvent` function.
 */
export const useFireEngagementPlatformAnalyticsEvents =
	(): EngagementPlatformAnalyticsHookScope => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const fireAnalyticsEvent = useCallback(
			<Attributes extends Record<string, unknown>>(
				eventPayload: EPEventPayload,
				attributes?: Attributes,
			) => {
				createAnalyticsEvent({
					...eventPayload,
					side: 'client',
					attributes: {
						...eventPayload.attributes,
						...attributes,
					},
				}).fire(EP_ANALYTICS_CHANNEL);
			},
			[createAnalyticsEvent],
		);

		return {
			fireAnalyticsEvent,
		};
	};
